/**
 * Textarea Component
 * 
 * A reusable textarea component with various states and styles.
 * Implements accessibility features and integrates with the design system.
 */
import { forwardRef, TextareaHTMLAttributes } from 'react'
import { cn, twStyles } from '../../utils/tailwind-utils'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Error message to display
   */
  error?: string
  
  /**
   * Label for the textarea
   */
  label?: string
  
  /**
   * Helper text to display below the textarea
   */
  helperText?: string
  
  /**
   * Whether the textarea is in a loading state
   */
  isLoading?: boolean
  
  /**
   * Additional CSS classes for the wrapper
   */
  wrapperClassName?: string
  
  /**
   * Whether to auto-resize the textarea based on content
   */
  autoResize?: boolean
  
  /**
   * Character count display
   */
  showCharCount?: boolean
  
  /**
   * Maximum character count
   */
  maxCharCount?: number
}

/**
 * Textarea component for multiline text entry with various states
 * 
 * @example
 * // Basic usage
 * <Textarea placeholder="Enter your message" />
 * 
 * @example
 * // With label and error
 * <Textarea 
 *   label="Message"
 *   error="Message is required"
 *   placeholder="Type your message here..."
 * />
 * 
 * @example
 * // With character count
 * <Textarea 
 *   showCharCount
 *   maxCharCount={500}
 *   placeholder="Limited to 500 characters..."
 * />
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      wrapperClassName,
      error,
      label,
      helperText,
      isLoading,
      disabled,
      autoResize,
      showCharCount,
      maxCharCount,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    // Determine if the textarea has an error state
    const hasError = !!error
    
    // Handle auto-resize
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) onChange(e)
      
      if (autoResize) {
        e.target.style.height = 'auto'
        e.target.style.height = `${e.target.scrollHeight}px`
      }
    }
    
    // Calculate character count
    const charCount = typeof value === 'string' ? value.length : 0
    const isOverLimit = maxCharCount !== undefined && charCount > maxCharCount
    
    return (
      <div className={cn('space-y-2', wrapperClassName)}>
        {/* Label */}
        {label && (
          <label 
            htmlFor={props.id} 
            className={cn(
              'block text-sm font-medium',
              hasError ? 'text-destructive' : 'text-foreground'
            )}
          >
            {label}
          </label>
        )}
        
        {/* Textarea wrapper */}
        <div className="relative">
          <textarea
            className={cn(
              twStyles.input.base,
              'min-h-[80px] resize-y',
              hasError && 'border-destructive focus-visible:ring-destructive',
              isOverLimit && 'border-destructive focus-visible:ring-destructive',
              isLoading && 'opacity-70',
              className
            )}
            disabled={disabled || isLoading}
            ref={ref}
            value={value}
            onChange={handleChange}
            {...props}
          />
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute right-3 top-3 text-muted-foreground">
              <svg 
                className="animate-spin h-5 w-5" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                ></circle>
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
        
        {/* Character count */}
        {showCharCount && (
          <div className="flex justify-end">
            <span 
              className={cn(
                'text-xs',
                isOverLimit ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {charCount}
              {maxCharCount !== undefined && ` / ${maxCharCount}`}
            </span>
          </div>
        )}
        
        {/* Error message or helper text */}
        {(error || helperText) && (
          <p 
            className={cn(
              'text-sm',
              hasError ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)

// Display name for debugging
Textarea.displayName = 'Textarea'

export default Textarea
